import React, { useMemo, useState } from 'react';
import { formatDecimal as fmt } from '../../helpers/paymentsHelper';

// Shared building blocks for the Weekly Overview tables (Executive + Invoicing).
// Both tables render the same sortable purple-chevron headers, the same eye
// expand/collapse icon, and sort/format their rows identically, so those pieces
// live here as one source rather than drifting across the two files.

export const HEADER_COLOR = '#6E6C71';
export const PURPLE = '#745D96';
// Eta-Grey token from Figma, used for the row eye (expand) block.
export const GREY = '#9B999E';
export const INACTIVE_CHEVRON = '#9C9C9C';

/**
 * Read a value from an object by a dot-delimited path, returning undefined for
 * any missing segment. Used so table columns can sort on nested payload keys
 * (e.g. `current.payout`, `toggle_table.trips`).
 *
 * @param {Object} obj - The row object.
 * @param {string} path - Dot-delimited key path.
 * @returns {*} The value at the path, or undefined.
 */
export const getByPath = (obj, path) =>
  path.split('.').reduce((acc, key) => (acc?.[key] ?? undefined), obj);

/**
 * Numeric-aware comparator. Nulls sort last; two numeric-looking values compare
 * as numbers; otherwise a locale, numeric-aware string compare is used.
 *
 * @param {*} a - First value.
 * @param {*} b - Second value.
 * @returns {number} Negative, zero, or positive per the standard sort contract.
 */
export const cmp = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  const aNum = Number(a);
  const bNum = Number(b);
  const bothNumbers = !Number.isNaN(aNum) && !Number.isNaN(bNum);
  if (bothNumbers) return aNum - bNum;

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

/**
 * Format a value as Rand currency (two decimals).
 *
 * Nullish renders as a dash, not R 0.00. The backend sends null for a figure it could not
 * fully price — a company missing a rate card — and an understated rand value is
 * indistinguishable from a real one, so it must not be shown as money. A genuine zero is
 * still sent as 0 and still renders as R 0.00.
 *
 * @param {number|string|null|undefined} value - The amount.
 * @returns {string} e.g. "R 1,234.00", or "—" when there is no figure.
 */
export const money = (value) => (value === null || value === undefined ? '—' : `R ${fmt(value)}`);

/**
 * Format a period-over-period variance, dashing when there is no figure.
 *
 * Mirrors `money`: the backend sends null for a variance it could not measure because the
 * money on one side was never fully priced. Rendering that as a bare "%" reads as a broken
 * cell, and rendering it as "0%" would claim the business did not move.
 *
 * Distinct from the occupancy percentage, which is always known and is shown rounded to a
 * whole number. Variances keep their two decimals.
 *
 * @param {number|string|null|undefined} value - The percentage.
 * @returns {string} e.g. "12.5%", or "—" when there is no figure.
 */
export const variancePercent = (value) => (value === null || value === undefined ? '—' : `${value}%`);

/**
 * Column-sort state for a table. Holds the `{ key, direction }` sort config,
 * exposes a click handler that toggles asc/desc on the active column, and
 * returns the rows sorted with {@link cmp}. Every sortable table in the payments
 * UI repeats this exact state/handler/memo trio, so it lives here once.
 *
 * @param {Array<Object>} rows - The rows to sort. Returned untouched until a column is picked.
 * @param {Object} [options]
 * @param {Function} [options.getValue] - Reads the sort value from a row given the sort key.
 *   Defaults to {@link getByPath} (dot-path lookup); pass `(row, key) => row[key]` for flat rows.
 * @returns {{sortConfig: {key: string, direction: string}, handleSort: Function, sortedRows: Array<Object>}}
 */
export const useTableSort = (rows, { getValue = getByPath } = {}) => {
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return rows;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => dir * cmp(getValue(a, sortConfig.key), getValue(b, sortConfig.key)));
  }, [rows, sortConfig]);

  return { sortConfig, handleSort, sortedRows };
};

const Chevron = ({ active, direction, want }) => (
  <path
    d={want === 'asc' ? 'M12 5l-5 5h10l-5-5z' : 'M12 19l5-5H7l5 5z'}
    fill={active && direction === want ? PURPLE : INACTIVE_CHEVRON}
  />
);

/**
 * A sortable header cell with the purple up/down chevron treatment, shared by
 * both Weekly tables so the chevron markup is defined once.
 *
 * @param {Object} props
 * @param {string} props.label - Visible column label.
 * @param {string} props.sortKey - Dot path used for sorting (getByPath).
 * @param {{key: string, direction: string}} props.sortConfig - Active sort state.
 * @param {(key: string) => void} props.onSort - Sort handler.
 * @param {string} [props.align='left'] - Text alignment for the cell.
 * @param {string} [props.testId] - data-testid for the header cell.
 * @returns {JSX.Element}
 */
export const SortableTh = ({ label, sortKey, sortConfig, onSort, align = 'left', testId, className = '' }) => (
  <th
    className={`border-b p-2 cursor-pointer text-${align} sticky top-0 z-10 bg-white ${className}`}
    style={{ color: HEADER_COLOR }}
    data-testid={testId}
    onClick={() => onSort(sortKey)}
  >
    {label}{' '}
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="32" className="inline-block ml-1">
      <Chevron active={sortConfig.key === sortKey} direction={sortConfig.direction} want="asc" />
      <Chevron active={sortConfig.key === sortKey} direction={sortConfig.direction} want="desc" />
    </svg>
  </th>
);

/**
 * The white eye glyph carried by the row expand/collapse button. Renders an
 * open eye when collapsed and a "closed" (lashes) eye when expanded.
 *
 * @param {Object} props
 * @param {boolean} props.expanded - Whether the row is currently expanded.
 * @returns {JSX.Element}
 */
export const EyeToggleIcon = ({ expanded }) =>
  expanded ? (
    <svg width="22" height="11" viewBox="0 0 20 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M19 1C16.6 3.667 13.6 5 10 5C6.4 5 3.4 3.667 1 1M1 7.00001L3.5 3.20001M18.9998 6.97601L16.5078 3.20001M7 9L7.5 5M13 9L12.5 5"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ) : (
    <svg width="22" height="15" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 7C8 7.53043 8.21071 8.03914 8.58579 8.41421C8.96086 8.78929 9.46957 9 10 9C10.5304 9 11.0391 8.78929 11.4142 8.41421C11.7893 8.03914 12 7.53043 12 7C12 6.46957 11.7893 5.96086 11.4142 5.58579C11.0391 5.21071 10.5304 5 10 5C9.46957 5 8.96086 5.21071 8.58579 5.58579C8.21071 5.96086 8 6.46957 8 7Z"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19 7C16.6 11 13.6 13 10 13C6.4 13 3.4 11 1 7C3.4 3 6.4 1 10 1C13.6 1 16.6 3 19 7Z"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

/**
 * The white download-tray glyph for the invoicing row export button.
 *
 * @returns {JSX.Element}
 */
export const DownloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
